// @ts-nocheck
import { Trans } from '@lingui/react'
import { OfferConditionTypeEnumV2 } from '@redteclab/api/clients/bully'
import { IconPresentGiftCouponSide } from '@redteclab/icons'
import { bool, InferProps, number, oneOf, shape, string } from 'prop-types'
import { type FunctionComponent } from 'react'
import * as React from 'react'
import { compose } from 'redux'

import { TENANT_CH, TENANT_IT } from 'shared/consts'
import { OFFER_PROMOTION_TYPES } from 'shared/consts/marketplace'
import {
  SUBSTITUTION_STATUS,
  SUBSTITUTION_STATUS_VALUES,
} from 'shared/consts/swap'
import {
  CART_FREE_GIFT_PROMOTION,
  EXPERIMENT_VARIATION,
  MARKETPLACE_HARMONIZATION,
} from 'shared/experiments/consts'
import { useGetExperiment } from 'shared/experiments/utils/experiments'
import { useGetExperimentNamePerTenant } from 'shared/experiments/utils/tenantBasedExperimentsUtils'
import MarketplacePromotionBadge from 'views/components/organisms/Cart/MarketplacePromotionBadge'
import VolumePricingBadge from 'views/components/organisms/Cart/VolumePricingBadge'
import ErxSubstitution from 'views/components/organisms/ErxSubstitution'
import ProductStatus from 'views/components/organisms/ProductStatus'
import Device from 'views/components/utils/Device'
import Tenant from 'views/components/utils/Tenant'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  isRedPointsActive,
  isUseMarketplaceVolumeBasedPricesEnabled,
} from 'views/utils/featureToggleUtils'
import {
  getColorByStatusReason,
  getLabelByNotOnStockMessage,
  getStatusByStatusReason,
} from 'views/utils/productStatusUtils'
import { mountPointsType } from 'views/utils/propTypes'

import AdditionalInformation from './AdditionalInformation'
import AvailabilityStatus from './AvailabilityStatus'
import MedicationType from './MedicationType'
import NowProductGroupLabel from './NowProductGroupLabel'
import PrescriptionIssuedTo from './PrescriptionIssuedTo'
import ProductInfoPrice from './ProductInfoPrice'
import ProductNameAndPrescriptionDate from './ProductNameAndPrescriptionDate'
import RedPointsLabel from './RedPointsLabel'
import Subtitle from './Subtitle'

import './ProductInfo.scss'

const SAME_OR_NEXT_DAY = 'sameday_nextday'

const Props = {
  rowNumber: number,
  nowProductGroup: string,
  nowProduct: bool,
  additionalInformation: string,
  productQuantity: number,
  unitPricing: string,
  currency: string,
  productId: string,
  productName: string,
  canonicalUrl: string,
  redPoints: number,
  price: number,
  subtitle: string,
  available: bool,
  outOfStockMessage: string,
  renderAvailability: bool,
  prescriptionDate: string,
  prescriptionIssuedTo: string,
  mountPoints: mountPointsType.isRequired,
  productType: string,
  isERx: bool,
  medicationType: string,
  displayCode: string,
  pzn: string,
  ean: string,
  isbn: string,
  upid: string,
  activeSubstance: shape({ name: string, strength: number, unit: string }),
  substitutionStatus: oneOf(SUBSTITUTION_STATUS_VALUES),
  originalPrescriptionPzn: string,
  originalPrescriptionProduct: shape({
    name: string,
    unit: string,
    unitQuantityString: string,
  }),
  offerId: string,
  publicRuntimeConfig: {
    pageProperties: {},
  },
}
const ProductInfo: FunctionComponent<InferProps<typeof Props>> = ({
  rowNumber,
  nowProductGroup,
  nowProduct,
  additionalInformation,
  productQuantity,
  unitPricing,
  productName,
  available,
  outOfStockMessage,
  productId,
  redPoints,
  subtitle,
  currency,
  price,
  renderAvailability,
  mountPoints,
  productType,
  prescriptionDate,
  prescriptionIssuedTo,
  isERx,
  medicationType,
  displayCode,
  pzn,
  ean,
  isbn,
  upid,
  activeSubstance,
  substitutionStatus,
  originalPrescriptionPzn,
  originalPrescriptionProduct,
  volumePricing,
  offerId,
  publicRuntimeConfig: {
    pageProperties: { featureToggles },
  },
  sellerPromotion,
}): React.ReactElement => {
  const redPointsIsActive = isRedPointsActive(featureToggles)

  const stockStatusByNotOnStockMessage =
    getLabelByNotOnStockMessage(outOfStockMessage)

  const stockStatus = {
    statusReason: getLabelByNotOnStockMessage(outOfStockMessage),
    statusColor: getColorByStatusReason(stockStatusByNotOnStockMessage),
    status: getStatusByStatusReason(stockStatusByNotOnStockMessage),
  }

  const isInNowProductGroup = nowProductGroup === SAME_OR_NEXT_DAY

  const cartFreeGiftPromotionExperiment = useGetExperiment(
    useGetExperimentNamePerTenant(CART_FREE_GIFT_PROMOTION)
  )
  const cartFreeGiftPromotionExperimentV1 =
    cartFreeGiftPromotionExperiment?.isEnabled &&
    cartFreeGiftPromotionExperiment?.variant === EXPERIMENT_VARIATION.V1

  const renderStockStatus = () =>
    outOfStockMessage ? (
      <ProductStatus stockStatus={stockStatus} />
    ) : (
      renderAvailability && <AvailabilityStatus available={available} />
    )

  // hardcoded so we don't rely on 'wsssh-3182' Optimizely flag forever
  const shouldShowSimpleSwapInfoIcon = false

  const isVolumePricingAvailable =
    isUseMarketplaceVolumeBasedPricesEnabled(featureToggles) &&
    volumePricing?.length > 1

  const isSellerPromotionMinimumSameItemQuantity =
    sellerPromotion?.conditionType ===
    OfferConditionTypeEnumV2.MinimumSameItemQuantity

  const marketplaceHarmonization = useGetExperiment(MARKETPLACE_HARMONIZATION)

  const isMarketplaceHarmonizationEnabled =
    marketplaceHarmonization.isEnabled &&
    marketplaceHarmonization.variant === EXPERIMENT_VARIATION.V1

  return (
    <div
      className="o-ProductInfo l-flex l-flex__column l-flex--start"
      data-qa-id="cart-entry-productInfo"
    >
      {cartFreeGiftPromotionExperimentV1 &&
        additionalInformation === OFFER_PROMOTION_TYPES.FREEBIE && (
          <>
            <Device mobile>
              <p className="u-font-style--italic u-color--primary u-font-weight-rebranding--medium o-ProductListing__freeInformation">
                <Trans id="product-listing.section.free" />
              </p>
            </Device>
            <div className="l-flex a-full-width@small-tiny o-ProductInfo__freebieContainer">
              <IconPresentGiftCouponSide
                size="large"
                width={20}
                height={20}
                className="u-color--primary u-margin-small--bottom u-margin-x-small--right"
              />
              <p className="o-ProductInfo__freebieInformation u-margin-small--bottom">
                <Trans id="product.listing.section.gift" />
              </p>
            </div>
          </>
        )}
      <ProductNameAndPrescriptionDate
        productId={productId}
        productName={productName}
        mountPoints={mountPoints}
        prescriptionDate={prescriptionDate}
        productType={productType}
        isERx={isERx}
        cartFreeGiftPromotionExperimentV1={cartFreeGiftPromotionExperimentV1}
      />
      <MedicationType
        eRxMedicationType={medicationType}
        pzn={pzn}
        activeSubstance={activeSubstance}
        isERx={isERx}
      />
      <Subtitle
        subtitle={subtitle}
        notFreebieExperiment={
          !cartFreeGiftPromotionExperimentV1 ||
          additionalInformation !== OFFER_PROMOTION_TYPES.FREEBIE
        }
        isERx={isERx}
        productId={productId}
        pzn={pzn}
        displayCode={displayCode}
        ean={ean}
        upid={upid}
        isbn={isbn}
      />
      {cartFreeGiftPromotionExperimentV1 &&
        additionalInformation === OFFER_PROMOTION_TYPES.FREEBIE && (
          <div className="u-margin--top@medium o-ProductInfo__list-item-freebie-experiment-quantity">
            <Trans id="general.label.quantity" />: {productQuantity}
          </div>
        )}
      {isERx &&
        shouldShowSimpleSwapInfoIcon &&
        substitutionStatus === SUBSTITUTION_STATUS.SUBSTITUTED &&
        pzn !== originalPrescriptionPzn && (
          <ErxSubstitution
            modalId={`swapped-product-${originalPrescriptionPzn}-${rowNumber}`}
            originalName={originalPrescriptionProduct.name}
            replacedName={productName}
          />
        )}
      {!isERx && (price || unitPricing) && (
        <ProductInfoPrice
          price={price}
          currency={currency}
          unitPricing={unitPricing}
        />
      )}
      {renderStockStatus()}
      <PrescriptionIssuedTo prescriptionIssuedTo={prescriptionIssuedTo} />
      <Tenant not tenant={[TENANT_IT, TENANT_CH]}>
        {() => (
          <NowProductGroupLabel
            upid={upid}
            isInNowProductGroup={isInNowProductGroup}
            nowProduct={nowProduct}
          />
        )}
      </Tenant>
      {redPointsIsActive && <RedPointsLabel redPoints={redPoints} />}

      {isVolumePricingAvailable && (
        <VolumePricingBadge
          upid={upid}
          selectedQuantity={productQuantity}
          volumePricing={volumePricing}
          offerId={offerId}
        />
      )}

      {isMarketplaceHarmonizationEnabled &&
        isSellerPromotionMinimumSameItemQuantity && (
          <MarketplacePromotionBadge sellerPromotion={sellerPromotion} />
        )}

      {!cartFreeGiftPromotionExperimentV1 &&
        additionalInformation === OFFER_PROMOTION_TYPES.FREEBIE && (
          <AdditionalInformation />
        )}
    </div>
  )
}

ProductInfo.propTypes = Props

ProductInfo.defaultProps = {
  rowNumber: 0,
  nowProductGroup: 'none',
  nowProduct: false,
  additionalInformation: null,
  renderAvailability: true,
  unitPricing: null,
  currency: 'EUR',
  productName: null,
  productId: null,
  redPoints: null,
  price: 0,
  subtitle: null,
  available: false,
  outOfStockMessage: '',
  prescriptionDate: null,
  prescriptionIssuedTo: null,
  productType: null,
  isERx: false,
  medicationType: null,
  displayCode: null,
  pzn: null,
  ean: null,
  isbn: null,
  upid: null,
  substitutionStatus: null,
  originalPrescriptionPzn: null,
  originalPrescriptionProduct: {},
  canonicalUrl: '',
  volumePricing: null,
  offerId: '',
}

export default compose(withShopAndRuntimeConfig, withMountPoints)(ProductInfo)
